import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ImagePlus, Trash2 } from "lucide-react";
import { assetUrl, useSite } from "@/lib/content";
import { adminApi, formatError } from "@/lib/adminApi";
import { Checkbox, LiveIndicator, SaveButton, SectionHeader, useSaver } from "@/pages/admin/ui";

const thumb = (g) => assetUrl(Object.values(g.variants || {})[0] || g.src);
const asMedia = (g) => ({ src: g.src, variants: g.variants, width: g.width, height: g.height, alt: g.alt, en: g.en || { alt: null, verified: false }, stock: !!g.stock, visible: true });

function MediaSlot({ label, item, testId }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-line bg-white p-3" data-testid={testId}>
      {item ? <img src={thumb(item)} alt="" className="h-14 w-20 rounded-lg object-cover" /> : <div className="h-14 w-20 rounded-lg bg-stone-100" />}
      <div className="min-w-0">
        <p className="text-sm font-semibold text-wood">{label}</p>
        <LiveIndicator live={!!item && !item.stock} testId={`${testId}-live`} />
      </div>
    </div>
  );
}

function GalleryRow({ g, i, count, onChange, onMove, onDelete, onHero, onWide }) {
  return (
    <li className="rounded-2xl border border-line bg-white p-4 grid gap-4 sm:grid-cols-[120px_1fr]" data-testid={`admin-gallery-item-${g.id}`}>
      <img src={thumb(g)} alt="" className="h-24 w-full sm:w-[120px] rounded-xl object-cover" />
      <div className="space-y-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <LiveIndicator live={g.visible !== false && !g.stock} testId={`admin-gallery-item-${g.id}-live`} />
          {!g.alt?.trim() && <span className="text-xs font-semibold text-[#B42318]">Alternatif metin eksik</span>}
        </div>
        <input value={g.alt || ""} placeholder="Alternatif metin (görseli kısaca anlatın)" onChange={(e) => onChange({ ...g, alt: e.target.value })} className="w-full rounded-xl border border-line px-4 py-2.5 text-base" aria-label="Alternatif metin" data-testid={`admin-gallery-item-${g.id}-alt`} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <input value={g.en?.alt || ""} placeholder="Alt text (English)" onChange={(e) => onChange({ ...g, en: { ...(g.en || { verified: false }), alt: e.target.value } })} className="flex-1 rounded-xl border border-line px-4 py-2.5 text-base" aria-label="İngilizce alternatif metin" data-testid={`admin-gallery-item-${g.id}-alt-en`} />
          <Checkbox id={`ge-${g.id}`} label="EN Doğrulandı" checked={g.en?.verified} onChange={(v) => onChange({ ...g, en: { ...(g.en || { alt: "" }), verified: v } })} testId={`admin-gallery-item-${g.id}-alt-en-verified`} />
        </div>
        <div className="flex flex-wrap items-center gap-x-5">
          <Checkbox id={`gv-${g.id}`} label="Görünür" checked={g.visible !== false} onChange={(v) => onChange({ ...g, visible: v })} testId={`admin-gallery-item-${g.id}-visible`} />
          <Checkbox id={`gs-${g.id}`} label="Temsili (stok) görsel" checked={g.stock} onChange={(v) => onChange({ ...g, stock: v })} testId={`admin-gallery-item-${g.id}-stock`} />
        </div>
        <div className="flex flex-wrap gap-2">
          <IconBtn label="Yukarı taşı" disabled={i === 0} onClick={() => onMove(-1)} testId={`admin-gallery-item-${g.id}-up`}><ArrowUp className="h-4 w-4" /></IconBtn>
          <IconBtn label="Aşağı taşı" disabled={i === count - 1} onClick={() => onMove(1)} testId={`admin-gallery-item-${g.id}-down`}><ArrowDown className="h-4 w-4" /></IconBtn>
          <button type="button" onClick={onHero} className="min-h-[40px] px-3 rounded-full border border-line text-sm font-medium hover:bg-cream-2" data-testid={`admin-gallery-item-${g.id}-set-hero`}>Ana görsel yap</button>
          <button type="button" onClick={onWide} className="min-h-[40px] px-3 rounded-full border border-line text-sm font-medium hover:bg-cream-2" data-testid={`admin-gallery-item-${g.id}-set-wide`}>Geniş bölüm görseli yap</button>
          <IconBtn label="Sil" onClick={onDelete} danger testId={`admin-gallery-item-${g.id}-delete`}><Trash2 className="h-4 w-4" /></IconBtn>
        </div>
      </div>
    </li>
  );
}

function IconBtn({ label, children, danger, ...rest }) {
  const { testId, ...props } = rest;
  return (
    <button type="button" aria-label={label} title={label} className={`inline-flex h-10 w-10 items-center justify-center rounded-full border disabled:opacity-40 ${danger ? "border-[#FECDCA] text-[#B42318] hover:bg-[#FEF3F2]" : "border-line hover:bg-cream-2"}`} data-testid={testId} {...props}>
      {children}
    </button>
  );
}

export default function GallerySection() {
  const { content } = useSite();
  const [items, setItems] = useState(() => content.gallery.map((g) => ({ ...g })));
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { save, saving } = useSaver();
  const media = content.media || {};

  const persist = (list) => save([["gallery", list]]);
  const update = (i, g) => setItems(items.map((x, j) => (j === i ? g : x)));
  const move = (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setItems(next);
  };
  const remove = async (i) => {
    const g = items[i];
    if (!window.confirm("Bu görsel galeriden kaldırılsın mı?")) return;
    const next = items.filter((_, j) => j !== i);
    if (await persist(next)) {
      setItems(next);
      if (g.src.startsWith("/api/media/")) adminApi.delete(`/admin/uploads/${g.id}`).catch(() => null);
    }
  };

  const onFiles = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await adminApi.post("/admin/uploads", fd);
        added.push(data);
      } catch (err) {
        toast.error(`${f.name}: ${formatError(err)}`);
      }
    }
    setUploading(false);
    if (added.length) {
      const next = [...added, ...items];
      setItems(next);
      if (await persist(next)) toast.message("Yüklenen görseller için alternatif metin eklemeyi unutmayın.");
    }
  };

  return (
    <section data-testid="admin-section-galeri">
      <SectionHeader title="Galeri" text="Gerçek fotoğraflar yükleyin (JPG, PNG, WebP; en fazla 15 MB). Görseller otomatik olarak WebP’ye dönüştürülüp farklı boyutlarda kaydedilir. “Temsili” işaretli görseller canlı sitede gösterilmez." />
      <div className="grid gap-3 sm:grid-cols-2 mb-6">
        <MediaSlot label="Ana sayfa giriş görseli" item={media.hero} testId="admin-media-hero" />
        <MediaSlot label="Geniş bölüm görseli" item={media.wide} testId="admin-media-wide" />
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={onFiles} data-testid="admin-gallery-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 min-h-[48px] px-6 rounded-full bg-honey-ink text-white font-semibold hover:opacity-90 disabled:opacity-60" data-testid="admin-gallery-upload-button">
          <ImagePlus className="h-4 w-4" aria-hidden="true" /> {uploading ? "Yükleniyor…" : "Fotoğraf yükle"}
        </button>
        <SaveButton onClick={() => persist(items)} saving={saving} testId="admin-save-galeri" label="Galeriyi kaydet" />
      </div>
      <ul className="space-y-3">
        {items.map((g, i) => (
          <GalleryRow key={g.id} g={g} i={i} count={items.length} onChange={(n) => update(i, n)} onMove={(d) => move(i, d)} onDelete={() => remove(i)}
            onHero={() => save([["media", { ...media, hero: asMedia(g) }]])} onWide={() => save([["media", { ...media, wide: asMedia(g) }]])} />
        ))}
      </ul>
    </section>
  );
}
